import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import {
  listNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api/notifications';
import type { Notification } from '../types/api';

// Top-right bell icon + dropdown (AppShell's top bar). Polls the unread
// count every 30s (no websockets in this app - same "keep it simple"
// choice as everywhere else); the list itself is only fetched once the
// dropdown is opened.
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const countQuery = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: getUnreadNotificationCount,
    refetchInterval: 30000,
  });
  const unreadCount = countQuery.data?.count ?? 0;

  const listQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: listNotifications,
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleSelect(n: Notification) {
    if (!n.read) markReadMutation.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        className="relative rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        // Opens downward, anchored to the right edge - the bell now lives in
        // the top bar (see AppShell), not the sidebar footer it was
        // originally built for. The old `bottom-full` (open upward) rendered
        // this entirely above the top of the viewport once the bell moved up
        // here and had no room above it to open into.
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5">
            <span className="text-sm font-semibold text-slate-900">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {listQuery.isLoading ? (
              <p className="px-3.5 py-6 text-center text-sm text-slate-400">Loading...</p>
            ) : !listQuery.data || listQuery.data.length === 0 ? (
              <p className="px-3.5 py-6 text-center text-sm text-slate-400">No notifications yet.</p>
            ) : (
              listQuery.data.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleSelect(n)}
                  className={`flex w-full flex-col gap-0.5 border-b border-slate-50 px-3.5 py-2.5 text-left last:border-0 hover:bg-slate-50 ${
                    n.read ? '' : 'bg-brand-50/40'
                  }`}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />}
                    {n.title}
                  </span>
                  <span className="line-clamp-2 text-xs text-slate-500">{n.body}</span>
                  <span className="text-[10px] text-slate-400">{new Date(n.createdAt).toLocaleString()}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
